import { useState } from "react";
import { platform } from "../platform";
import type { AuthError } from "../platform/PlatformAdapter";

interface AuthPanelProps {
  onClose: () => void;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.5rem 0.75rem",
  marginBottom: "0.75rem",
  background: "rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: "6px",
  color: "#eee",
  fontSize: "0.95rem",
  boxSizing: "border-box",
  fontFamily: "var(--theme-font-family, inherit)",
};

export function AuthPanel({ onClose }: AuthPanelProps) {
  const [mode, setMode] = useState<"login" | "register" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [registered, setRegistered] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const result =
      mode === "login"
        ? await platform.login(email, password)
        : await platform.register(email, password, dateOfBirth);
    setPending(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (mode === "register") {
      setRegistered(true);
    } else {
      onClose();
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    await platform.forgotPassword(email);
    setPending(false);
    setResetRequested(true);
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div className="theme-panel" style={{ width: "min(90%, 380px)", padding: "2rem" }}>
        {registered || resetRequested ? (
          <>
            <h2 style={{ margin: "0 0 1rem", fontSize: "1.3rem" }}>Check your email</h2>
            <p style={{ opacity: 0.8, fontSize: "0.9rem", lineHeight: 1.5 }}>
              {registered ? (
                <>
                  We sent a verification link to <strong>{email}</strong>. Click it, then log in.
                </>
              ) : (
                <>
                  If an account exists for <strong>{email}</strong>, we sent a password reset link.
                </>
              )}
            </p>
            <button
              onClick={onClose}
              className="theme-button"
              style={{ marginTop: "1rem", padding: "0.5rem 1.25rem" }}
            >
              Close
            </button>
          </>
        ) : mode === "forgot" ? (
          <form onSubmit={handleForgotSubmit}>
            <h2 style={{ margin: "0 0 1.5rem", fontSize: "1.3rem" }}>Reset Password</h2>

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />

            <button
              type="submit"
              disabled={pending}
              className="theme-button"
              style={{
                width: "100%",
                padding: "0.6rem",
                fontWeight: "bold",
                background: "var(--theme-accent, #7ec8e3)",
                color: "#111",
                marginBottom: "0.75rem",
                opacity: pending ? 0.6 : 1,
                cursor: pending ? "not-allowed" : "pointer",
              }}
            >
              {pending ? "..." : "Send reset link"}
            </button>

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
              <button
                type="button"
                onClick={() => setMode("login")}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--theme-accent, #7ec8e3)",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Back to log in
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{ background: "none", border: "none", color: "#999", cursor: "pointer", padding: 0 }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ margin: "0 0 1.5rem", fontSize: "1.3rem" }}>
              {mode === "login" ? "Log In" : "Create Account"}
            </h2>

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
              minLength={8}
            />
            {mode === "register" && (
              <>
                <label
                  style={{ display: "block", fontSize: "0.75rem", opacity: 0.6, marginBottom: "0.25rem" }}
                >
                  Date of birth
                </label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  style={inputStyle}
                  required
                />
              </>
            )}

            {error && (
              <div
                style={{
                  background: "rgba(220, 50, 50, 0.15)",
                  border: "1px solid rgba(220, 50, 50, 0.3)",
                  borderRadius: "8px",
                  padding: "0.6rem 0.8rem",
                  marginBottom: "0.75rem",
                  color: "#ff7f7f",
                  fontSize: "0.8rem",
                }}
              >
                {error.message}
                {error.supportLink && (
                  <>
                    {" "}
                    <a href={error.supportLink} style={{ color: "#ff7f7f" }}>
                      Contact support
                    </a>
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              className="theme-button"
              style={{
                width: "100%",
                padding: "0.6rem",
                fontWeight: "bold",
                background: "var(--theme-accent, #7ec8e3)",
                color: "#111",
                marginBottom: "0.75rem",
                opacity: pending ? 0.6 : 1,
                cursor: pending ? "not-allowed" : "pointer",
              }}
            >
              {pending ? "..." : mode === "login" ? "Log In" : "Create Account"}
            </button>

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
              <button
                type="button"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--theme-accent, #7ec8e3)",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {mode === "login" ? "Need an account? Register" : "Already have an account? Log in"}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{ background: "none", border: "none", color: "#999", cursor: "pointer", padding: 0 }}
              >
                Cancel
              </button>
            </div>
            {mode === "login" && (
              <div style={{ marginTop: "0.6rem", fontSize: "0.8rem" }}>
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setError(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
